import React from 'react';

interface NonSkVerifyStepProps {
  receiptData: any;
  selectedCountry: string;
  onReceiptDataChange: (data: any) => void;
  isProcessing: boolean;
  onSubmit: (formData: any) => void;
  onBack: () => void;
}

const NonSkVerifyStep: React.FC<NonSkVerifyStepProps> = ({
  receiptData,
  selectedCountry,
  onReceiptDataChange,
  isProcessing,
  onSubmit,
  onBack
}) => {
  const receipt = receiptData.receipt;

  const handleOrganizationChange = (field: string, value: string) => {
    const newReceipt = { ...receipt };
    newReceipt.organization[field] = value;
    onReceiptDataChange({ ...receiptData, receipt: newReceipt });
  };

  const handleReceiptFieldChange = (field: string, value: any) => {
    const newReceipt = { ...receipt };
    newReceipt[field] = value;
    onReceiptDataChange({ ...receiptData, receipt: newReceipt });
  };

  const handleItemChange = (index: number, field: string, value: any) => {
    const newReceipt = { ...receipt };
    newReceipt.items[index][field] = value;

    // Recalculate total price if quantity or unitPrice changes
    if (field === 'quantity' || field === 'unitPrice') {
      const quantity = field === 'quantity' ? value : newReceipt.items[index].quantity;
      const unitPrice = field === 'unitPrice' ? value : newReceipt.items[index].unitPrice;
      newReceipt.items[index].price = (quantity || 0) * (unitPrice || 0);
    }

    // Recalculate unit price if total price changes
    if (field === 'price' && newReceipt.items[index].quantity > 0) {
      newReceipt.items[index].unitPrice = value / newReceipt.items[index].quantity;
    }

    onReceiptDataChange({ ...receiptData, receipt: newReceipt });
  };

  const handleDeleteItem = (index: number) => {
    const newReceipt = { ...receipt };
    newReceipt.items.splice(index, 1);
    onReceiptDataChange({ ...receiptData, receipt: newReceipt });
  };

  const handleAddItem = () => {
    const newReceipt = { ...receipt };
    if (!newReceipt.items) newReceipt.items = [];
    newReceipt.items.push({
      name: '',
      quantity: 1,
      unit: 'ks',
      unitPrice: 0,
      price: 0,
      vatRate: 20,
      vatAmount: 0
    });
    onReceiptDataChange({ ...receiptData, receipt: newReceipt });
  };

  const handleSubmit = () => {
    const formData = {
      receiptId: receipt.receiptId,
      issueDate: receipt.issueDate,
      totalPrice: receipt.totalPrice,
      currency: receipt.currency,
      vatAmountTotal: receipt.vatAmountTotal,
      country_code: selectedCountry,
      organization: receipt.organization,
      items: receipt.items,
      discounts: receipt.discounts || []
    };
    onSubmit(formData);
  };

  return (
    <div style={{
      fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
      background: '#667eea',
      padding: '15px',
      minHeight: '100vh',
      overflowX: 'hidden'
    }}>
      <div style={{
        maxWidth: '100%',
        width: '100%',
        margin: '0 auto',
        background: '#fff',
        borderRadius: '20px',
        padding: 'clamp(16px, 4vw, 32px)',
        boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1)',
        boxSizing: 'border-box'
      }}>

        {/* Header */}
        <div style={{ textAlign: 'center', marginBottom: '30px' }}>
          <h1 style={{
            fontSize: 'clamp(28px, 6vw, 36px)',
            fontWeight: 700,
            color: '#111827',
            margin: '0 0 10px 0'
          }}>
            Overenie údajov
          </h1>
          <p style={{ color: '#6b7280', fontSize: '18px', margin: 0 }}>
            Skontroluj a uprav údaje z bločka pred uložením
          </p>
        </div>

        {/* Organization Section */}
        <div style={{
          background: '#fff',
          border: '2px solid #e5e7eb',
          borderRadius: '12px',
          padding: '20px',
          marginBottom: '20px'
        }}>
          <h3 style={{ color: '#111827', marginBottom: '16px', fontSize: '18px', fontWeight: 700 }}>📍 Obchod</h3>
          <div style={{ display: 'grid', gap: '14px' }}>
            <div>
              <label style={{
                display: 'block',
                fontSize: '13px',
                fontWeight: 600,
                color: '#374151',
                marginBottom: '6px'
              }}>
                Názov obchodu
              </label>
              <input
                type="text"
                value={receipt.organization?.name || ''}
                onChange={(e) => handleOrganizationChange('name', e.target.value)}
                placeholder="Napr. Tesco"
                style={{
                  width: '100%',
                  padding: '12px',
                  border: '1px solid #d1d5db',
                  borderRadius: '8px',
                  fontSize: '16px',
                  boxSizing: 'border-box',
                  fontWeight: 500
                }}
              />
            </div>
            <div>
              <label style={{
                display: 'block',
                fontSize: '13px',
                fontWeight: 600,
                color: '#374151',
                marginBottom: '6px'
              }}>
                Adresa
              </label>
              <input
                type="text"
                value={`${receipt.organization?.streetName || ''} ${receipt.organization?.buildingNumber || ''}`.trim()}
                onChange={(e) => {
                  const parts = e.target.value.split(' ');
                  const newReceipt = { ...receipt };
                  newReceipt.organization.streetName = parts.slice(0, -1).join(' ');
                  newReceipt.organization.buildingNumber = parts[parts.length - 1] || '';
                  onReceiptDataChange({ ...receiptData, receipt: newReceipt });
                }}
                placeholder="Ulica a číslo"
                style={{
                  width: '100%',
                  padding: '12px',
                  border: '1px solid #d1d5db',
                  borderRadius: '8px',
                  fontSize: '16px',
                  boxSizing: 'border-box'
                }}
              />
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: '12px' }}>
              <div>
                <label style={{
                  display: 'block',
                  fontSize: '13px',
                  fontWeight: 600,
                  color: '#374151',
                  marginBottom: '6px'
                }}>
                  Mesto
                </label>
                <input
                  type="text"
                  value={receipt.organization?.municipality || ''}
                  onChange={(e) => handleOrganizationChange('municipality', e.target.value)}
                  placeholder="Mesto"
                  style={{
                    width: '100%',
                    padding: '12px',
                    border: '1px solid #d1d5db',
                    borderRadius: '8px',
                    fontSize: '16px',
                    boxSizing: 'border-box'
                  }}
                />
              </div>
              <div>
                <label style={{
                  display: 'block',
                  fontSize: '13px',
                  fontWeight: 600,
                  color: '#374151',
                  marginBottom: '6px'
                }}>
                  PSČ
                </label>
                <input
                  type="text"
                  value={receipt.organization?.postalCode || ''}
                  onChange={(e) => handleOrganizationChange('postalCode', e.target.value)}
                  placeholder="12345"
                  style={{
                    width: '100%',
                    padding: '12px',
                    border: '1px solid #d1d5db',
                    borderRadius: '8px',
                    fontSize: '16px',
                    boxSizing: 'border-box'
                  }}
                />
              </div>
            </div>
          </div>
        </div>

        {/* Receipt Info */}
        <div style={{
          background: '#fff',
          border: '2px solid #e5e7eb',
          borderRadius: '12px',
          padding: '20px',
          marginBottom: '20px'
        }}>
          <h3 style={{ color: '#111827', marginBottom: '16px', fontSize: '18px', fontWeight: 700 }}>🧾 Údaje bločka</h3>
          <div style={{ display: 'grid', gap: '14px' }}>
            <div>
              <label style={{
                display: 'block',
                fontSize: '13px',
                fontWeight: 600,
                color: '#374151',
                marginBottom: '6px'
              }}>
                Dátum a čas nákupu
              </label>
              <input
                type="text"
                value={receipt.issueDate || ''}
                onChange={(e) => handleReceiptFieldChange('issueDate', e.target.value)}
                placeholder="DD.MM.YYYY HH:MM:SS"
                style={{
                  width: '100%',
                  padding: '12px',
                  border: '1px solid #d1d5db',
                  borderRadius: '8px',
                  fontSize: '16px',
                  boxSizing: 'border-box'
                }}
              />
            </div>
            <div>
              <label style={{
                display: 'block',
                fontSize: '13px',
                fontWeight: 600,
                color: '#374151',
                marginBottom: '6px'
              }}>
                Celková suma
              </label>
              <input
                type="number"
                value={receipt.totalPrice || ''}
                onChange={(e) => handleReceiptFieldChange('totalPrice', parseFloat(e.target.value) || 0)}
                placeholder="0.00"
                step="0.01"
                style={{
                  width: '100%',
                  padding: '12px',
                  border: '1px solid #d1d5db',
                  borderRadius: '8px',
                  fontSize: '16px',
                  boxSizing: 'border-box',
                  fontWeight: 600
                }}
              />
            </div>
          </div>
        </div>

        {/* Editable Items Section - Mobile-First Card Design */}
        <div style={{ marginBottom: '30px' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '15px' }}>
            <h3 style={{ color: '#111827', margin: 0 }}>🛒 Položky</h3>
            <span style={{
              background: '#f3f4f6',
              color: '#374151',
              padding: '4px 12px',
              borderRadius: '12px',
              fontSize: '14px',
              fontWeight: 600
            }}>
              {receipt.items?.length || 0} ks
            </span>
          </div>

          <div style={{
            maxHeight: '500px',
            overflowY: 'auto',
            display: 'flex',
            flexDirection: 'column',
            gap: '12px'
          }}>
            {(receipt.items || []).map((item: any, index: number) => (
              <div key={index} style={{
                background: '#fff',
                border: '2px solid #e5e7eb',
                borderRadius: '12px',
                padding: '16px',
                position: 'relative'
              }}>
                {/* Card Header with Item Number and Delete */}
                <div style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  marginBottom: '12px'
                }}>
                  <span style={{
                    fontSize: '13px',
                    fontWeight: 700,
                    color: '#6b7280',
                    textTransform: 'uppercase',
                    letterSpacing: '0.5px'
                  }}>
                    Položka #{index + 1}
                  </span>
                  <button
                    onClick={() => handleDeleteItem(index)}
                    style={{
                      padding: '6px 12px',
                      background: '#fee2e2',
                      color: '#dc2626',
                      border: 'none',
                      borderRadius: '6px',
                      fontSize: '13px',
                      fontWeight: 600,
                      cursor: 'pointer',
                      transition: 'all 0.2s'
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.background = '#fecaca';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.background = '#fee2e2';
                    }}
                  >
                    🗑️ Zmazať
                  </button>
                </div>

                {/* Item Name */}
                <div style={{ marginBottom: '12px' }}>
                  <label style={{
                    display: 'block',
                    fontSize: '13px',
                    fontWeight: 600,
                    color: '#374151',
                    marginBottom: '6px'
                  }}>
                    Názov položky
                  </label>
                  <input
                    type="text"
                    value={item.name || ''}
                    onChange={(e) => handleItemChange(index, 'name', e.target.value)}
                    placeholder="Napr. Chlieb"
                    style={{
                      width: '100%',
                      padding: '12px',
                      border: '1px solid #d1d5db',
                      borderRadius: '8px',
                      fontSize: '16px',
                      boxSizing: 'border-box',
                      fontWeight: 500
                    }}
                  />
                </div>

                {/* Quantity and Unit Row */}
                <div style={{
                  display: 'grid',
                  gridTemplateColumns: '2fr 1fr',
                  gap: '12px',
                  marginBottom: '12px'
                }}>
                  <div>
                    <label style={{
                      display: 'block',
                      fontSize: '13px',
                      fontWeight: 600,
                      color: '#374151',
                      marginBottom: '6px'
                    }}>
                      Počet
                    </label>
                    <input
                      type="number"
                      value={item.quantity || ''}
                      onChange={(e) => handleItemChange(index, 'quantity', parseFloat(e.target.value) || 0)}
                      placeholder="1"
                      step="0.1"
                      style={{
                        width: '100%',
                        padding: '12px',
                        border: '1px solid #d1d5db',
                        borderRadius: '8px',
                        fontSize: '16px',
                        boxSizing: 'border-box'
                      }}
                    />
                  </div>
                  <div>
                    <label style={{
                      display: 'block',
                      fontSize: '13px',
                      fontWeight: 600,
                      color: '#374151',
                      marginBottom: '6px'
                    }}>
                      Jednotka
                    </label>
                    <input
                      type="text"
                      value={item.unit || ''}
                      onChange={(e) => handleItemChange(index, 'unit', e.target.value)}
                      placeholder="ks"
                      style={{
                        width: '100%',
                        padding: '12px',
                        border: '1px solid #d1d5db',
                        borderRadius: '8px',
                        fontSize: '16px',
                        boxSizing: 'border-box'
                      }}
                    />
                  </div>
                </div>

                {/* Unit Price */}
                <div style={{ marginBottom: '12px' }}>
                  <label style={{
                    display: 'block',
                    fontSize: '13px',
                    fontWeight: 600,
                    color: '#374151',
                    marginBottom: '6px'
                  }}>
                    Cena za kus
                  </label>
                  <input
                    type="number"
                    value={item.unitPrice || ''}
                    onChange={(e) => handleItemChange(index, 'unitPrice', parseFloat(e.target.value) || 0)}
                    placeholder="0.00"
                    step="0.01"
                    style={{
                      width: '100%',
                      padding: '12px',
                      border: '1px solid #d1d5db',
                      borderRadius: '8px',
                      fontSize: '16px',
                      boxSizing: 'border-box'
                    }}
                  />
                </div>

                {/* Total Price Display */}
                <div style={{
                  background: '#f0fdf4',
                  border: '2px solid #10b981',
                  borderRadius: '8px',
                  padding: '12px',
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center'
                }}>
                  <span style={{
                    fontSize: '14px',
                    fontWeight: 600,
                    color: '#047857'
                  }}>
                    Celková cena
                  </span>
                  <input
                    type="number"
                    value={item.price || ''}
                    onChange={(e) => handleItemChange(index, 'price', parseFloat(e.target.value) || 0)}
                    placeholder="0.00"
                    step="0.01"
                    style={{
                      width: '120px',
                      padding: '8px 12px',
                      border: '1px solid #10b981',
                      borderRadius: '6px',
                      fontSize: '18px',
                      fontWeight: 700,
                      color: '#047857',
                      textAlign: 'right',
                      boxSizing: 'border-box',
                      background: '#fff'
                    }}
                  />
                </div>
              </div>
            ))}
          </div>

          {/* Add Item Button */}
          <button
            onClick={handleAddItem}
            style={{
              width: '100%',
              marginTop: '12px',
              padding: '14px 20px',
              background: '#10b981',
              color: '#fff',
              border: 'none',
              borderRadius: '12px',
              fontSize: '16px',
              fontWeight: 600,
              cursor: 'pointer',
              transition: 'all 0.2s'
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.background = '#059669';
              e.currentTarget.style.transform = 'translateY(-1px)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.background = '#10b981';
              e.currentTarget.style.transform = 'translateY(0)';
            }}
          >
            ➕ Pridať položku
          </button>
        </div>

        {/* Action Buttons */}
        <div style={{ display: 'flex', gap: '15px', justifyContent: 'center', flexWrap: 'wrap' }}>
          <button
            onClick={onBack}
            disabled={isProcessing}
            style={{
              flex: '1 1 auto',
              minWidth: '120px',
              padding: '16px 24px',
              background: '#6b7280',
              color: '#fff',
              border: 'none',
              borderRadius: '12px',
              fontSize: '16px',
              fontWeight: 600,
              cursor: isProcessing ? 'not-allowed' : 'pointer',
              opacity: isProcessing ? 0.6 : 1,
              boxSizing: 'border-box'
            }}
          >
            ← Späť
          </button>
          <button
            onClick={handleSubmit}
            disabled={isProcessing}
            style={{
              flex: '2 1 auto',
              minWidth: '160px',
              padding: '16px 32px',
              background: isProcessing ? '#6b7280' : '#10b981',
              color: '#fff',
              border: 'none',
              borderRadius: '12px',
              fontSize: '16px',
              fontWeight: 600,
              cursor: isProcessing ? 'not-allowed' : 'pointer',
              opacity: isProcessing ? 0.6 : 1,
              boxSizing: 'border-box'
            }}
          >
            {isProcessing ? 'Ukladám...' : '✓ Uložiť bloček'}
          </button>
        </div>

        {/* Footer */}
        <div style={{
          textAlign: 'center',
          marginTop: '30px',
          color: '#9ca3af',
          fontSize: '14px'
        }}>
          V4 • 🌍 Neslovenský bloček • smartblocek.sk byTomasko
        </div>
      </div>
    </div>
  );
};

export default NonSkVerifyStep;
